/**
 * 📦 Archive Modal Component
 * عرض وإدارة الموظفين المؤرشفين
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Archive,
  X,
  Download,
  User,
  RotateCcw,
  Trash2,
  Eye,
  Building,
  Users,
  Calendar,
  CheckCircle,
} from "../../constants/icons";
import { fetchEmployeeProfilePicAPI } from "../../services/employeeService";

const genderAvatarBg = (gender) => {
  if (gender === "Male") return "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)";
  if (gender === "Female") return "linear-gradient(135deg, #f472b6 0%, #db2777 100%)";
  return "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)";
};

// Font sizes based on screen size
const fontMap = {
  xs:  { subtitle: "0.875rem", body: "0.75rem",   small: "0.6875rem", tiny: "0.625rem"  },
  sm:  { subtitle: "1rem",     body: "0.875rem",  small: "0.75rem",   tiny: "0.6875rem" },
  md:  { subtitle: "1.125rem", body: "0.9375rem", small: "0.8125rem", tiny: "0.75rem"   },
  lg:  { subtitle: "1.25rem",  body: "1rem",      small: "0.875rem",  tiny: "0.8125rem" },
  xl:  { subtitle: "1.375rem", body: "1rem",      small: "0.875rem",  tiny: "0.8125rem" },
  xxl: { subtitle: "1.5rem",   body: "1.125rem",  small: "1rem",      tiny: "0.875rem"  },
};

export const ArchiveModal = ({
  archivedEmployees = [],
  archivedCount = 0,
  setShowArchive = () => {},
  archiveSearch = "",
  setArchiveSearch = () => {},
  archiveFilterCompany = "",
  setArchiveFilterCompany = () => {},
  archiveFilterCompanySearch = "",
  setArchiveFilterCompanySearch = () => {},
  archiveFilterWorkingCompany = "",
  setArchiveFilterWorkingCompany = () => {},
  archiveFilterWorkingCompanySearch = "",
  setArchiveFilterWorkingCompanySearch = () => {},
  archiveFilterDepartment = "",
  setArchiveFilterDepartment = () => {},
  archiveFilterDepartmentSearch = "",
  setArchiveFilterDepartmentSearch = () => {},
  archiveFilterDateFrom = "",
  setArchiveFilterDateFrom = () => {},
  archiveFilterDateTo = "",
  setArchiveFilterDateTo = () => {},
  isAdminRole = false,
  loading = false,
  handleExportArchive = () => {},
  handleRestoreEmployee = () => {},
  handleDeleteEmployee = () => {},
  handleRestoreAllArchivedEmployees = () => {},
  handleDeleteAllArchived = () => {},
  clearArchiveFilters = () => {},
  getStatusBadge = () => null,
  theme = "light",
  language = "en",
  screenSize = "md",
  text = {},
  showAlert = () => {},
  showConfirm = () => {},
  canDeleteEmployee = false,
  canRestoreEmployee = false,
  ReusableDropdown = () => null,
  filteredArchivedEmployees = [],
  filterOptions = {},
  archivedThisMonth = 0,
}) => {
  const fonts = fontMap[screenSize] || fontMap.lg;
  const isDark = theme === "dark";

  // Lazily load real profile pictures for visible rows only (fast + cached, initials show instantly)
  const [profilePics, setProfilePics] = useState({});
  const [loadingPics, setLoadingPics] = useState(new Set());
  const picCacheRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    const codes = Array.from(
      new Set(
        filteredArchivedEmployees
          .map((e) => e.infinityCode || e.id)
          .filter((code) => code && !picCacheRef.current.has(code))
      )
    );

    const CONCURRENCY = 6;
    const loadOne = async (code) => {
      const url = await fetchEmployeeProfilePicAPI(code, picCacheRef, loadingPics, setLoadingPics);
      if (!cancelled && url) {
        setProfilePics((prev) => (prev[code] ? prev : { ...prev, [code]: url }));
      }
    };

    (async () => {
      for (let i = 0; i < codes.length && !cancelled; i += CONCURRENCY) {
        await Promise.all(codes.slice(i, i + CONCURRENCY).map(loadOne));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filteredArchivedEmployees]);

  // Color palette
  const colors = {
    bg:       isDark ? "#0f172a" : "#ffffff",
    surface:  isDark ? "#1e293b" : "#f8fafc",
    surface2: isDark ? "#162032" : "#f1f5f9",
    border:   isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)",
    text:     isDark ? "#f1f5f9" : "#0f172a",
    subtext:  isDark ? "#94a3b8" : "#64748b",
    muted:    isDark ? "#475569" : "#cbd5e1",
  };

  // Spacing
  const spacing = {
    margin: screenSize === "xs" ? "1rem" : "1.5rem",
    gap: screenSize === "xs" ? "0.75rem" : "1rem",
    padding: screenSize === "xs" ? "1rem" : "1.5rem",
  };

  // ✅ Internal styles (similar to other modals)
  const internalStyles = {
    tableContainer: {
      overflowX: "auto",
      marginTop: spacing.margin,
      borderRadius: "12px",
      border: `1px solid ${colors.border}`,
      background: colors.surface,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: screenSize === "xs" ? "600px" : "100%",
    },
    tableHeader: {
      background: isDark ? "#1e293b" : "#f1f5f9",
      color: colors.subtext,
      padding: screenSize === "xs" ? "0.75rem 0.5rem" : "1rem 0.75rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderBottom: `1px solid ${colors.border}`,
      whiteSpace: "nowrap",
    },
    tableCell: {
      padding: screenSize === "xs" ? "0.75rem 0.5rem" : "1rem 0.75rem",
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
      fontSize: fonts.body,
      fontWeight: "500",
      textAlign: "center",
      transition: "all 0.2s ease",
    },
    tableRow: {
      transition: "all 0.2s ease",
      cursor: "pointer",
    },
    actionBtn: {
      background: isDark ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
      color: isDark ? "#f1f5f9" : "#334155",
      border: "1px solid rgba(148, 163, 184, 0.3)",
      padding: screenSize === "xs" ? "0.375rem 0.5rem" : "0.5rem 0.75rem",
      borderRadius: "0.5rem",
      cursor: "pointer",
      fontSize: fonts.small,
      fontWeight: "500",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: screenSize === "xs" ? "2rem" : "2.25rem",
      height: screenSize === "xs" ? "2rem" : "2.25rem",
    },
    viewBtn: {},
    editBtn: {},
    deleteBtn: {},
    restoreBtn: {},
    secondaryBtn: {
      background: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
      color: isDark ? "white" : "#1a1a1a",
      border: isDark ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.1)",
      padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
      borderRadius: "0.625rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },
  };

  // ✅ Compute archive options from props or fallback to local computation
  const archiveCompanyOptions = filterOptions.companies || [...new Set(archivedEmployees.map((e) => e.company).filter((v) => v && v !== "N/A"))];
  const archiveWorkingCompanyOptions = filterOptions.workingCompanies || [...new Set(archivedEmployees.map((e) => e.workingCompany).filter(Boolean))];
  const archiveDepartmentOptions = filterOptions.departments || [...new Set(archivedEmployees.map((e) => e.department).filter((v) => v && v !== "N/A"))];

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          animation: "fadeIn 300ms ease-out",
        }}
        onClick={() => setShowArchive(false)}
      />

      {/* Modal Container */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10000,
          width: screenSize === "xs" ? "100%" : "92%",
          maxWidth: 1200,
          maxHeight: "92vh",
          background: isDark
            ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          color: isDark ? "#e6eef8" : "#0f172a",
          borderRadius: 16,
          boxShadow: isDark
            ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
            : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(148,163,184,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          direction: language === "ar" ? "rtl" : "ltr",
          animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div
            style={{
              padding: "32px 32px 20px",
              paddingBottom: 20,
              borderBottom: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.08)",
              flexShrink: 0,
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowArchive(false)}
              style={{
                position: "absolute",
                top: 14,
                right: language === "ar" ? "auto" : 14,
                left: language === "ar" ? 14 : "auto",
                width: 40,
                height: 40,
                borderRadius: 10,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
                border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
                cursor: "pointer",
                transition: "all 200ms ease",
                zIndex: 20,
                color: isDark ? "#94a3b8" : "#475569",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <X size={20} />
            </button>
            
            <h2
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                margin: 0,
                fontSize: fonts.subtitle,
                fontWeight: "700",
                background: isDark 
                  ? "#d6d4d4"
                  : "#000000",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              <Archive size={24} style={{ color: isDark ? "#ffffff" : "#000000" }} />
              <span>{text[language].viewArchive} ({archivedCount})</span>
            </h2>
          </div>


          {/* Scrollable Content */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "0 32px 32px",
              scrollbarWidth: "thin",
              scrollbarColor: isDark ? "#334155 transparent" : "#cbd5e1 transparent",
            }}
          >

        {/* Archive Description */}
        <div
          style={{
            background: isDark
              ? "rgba(107, 114, 128, 0.1)"
              : "rgba(107, 114, 128, 0.05)",
            border: isDark
              ? "1px solid rgba(107, 114, 128, 0.2)"
              : "1px solid rgba(107, 114, 128, 0.15)",
            borderRadius: 12,
            padding: "1rem 1.5rem",
            marginBottom: "1.5rem",
            marginTop: spacing.margin,
            display: "flex",
            alignItems: "center",
            gap: spacing.gap,
            flexDirection: screenSize === "xs" ? "column" : "row",
            textAlign: screenSize === "xs" ? "center" : "left",
          }}
        >
          <Archive size={screenSize === "xs" ? 18 : 20} color="#6b7280" />
          <div
            style={{
              color:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.8)"
                  : "rgba(26, 26, 26, 0.7)",
              fontSize: fonts.body,
              lineHeight: "1.5",
            }}
          >
            {text[language].archivedDescription}
          </div>
        </div>

        {/* Archive Statistics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: screenSize === "xs" ? "1fr" : "repeat(2, 1fr)",
            gap: spacing.gap,
            marginBottom: spacing.margin,
          }}
        >
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(107, 114, 128, 0.1)"
                  : "rgba(107, 114, 128, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {archivedCount}
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].totalArchived}
            </div>
          </div>
          <div
            style={{
              background:
                theme === "dark"
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.05)",
              padding: spacing.padding,
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#22c55e",
                fontSize: screenSize === "xs" ? "1.5rem" : "1.5rem",
                fontWeight: "700",
                marginBottom: "0.25rem",
              }}
            >
              {
                archivedEmployees.filter(
                  (emp) =>
                    emp.archivedDate &&
                    new Date(emp.archivedDate) >
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                ).length
              }
            </div>
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.small,
                fontWeight: "500",
              }}
            >
              {text[language].archivedThisMonth}
            </div>
          </div>
        </div>


        {/* Archive Filters - Professional Style */}
        <div
          style={{
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.8) 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: isDark 
              ? "1px solid rgba(100,116,139,0.25)" 
              : "1px solid rgba(0,0,0,0.08)",
            borderRadius: 16,
            padding: screenSize === "xs" ? "1.25rem 1rem" : "2rem 2.5rem",
            marginBottom: "2rem",
            direction: language === "ar" ? "rtl" : "ltr",
            boxShadow: isDark
              ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.1)"
              : "0 8px 32px rgba(0,0,0,0.08), inset 0 1px 1px rgba(255,255,255,0.6)",
            position: "relative",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Filter Header */}
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            gap: "1rem", 
            marginBottom: "1.75rem",
            flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: screenSize === "xs" ? 36 : 44,
                  height: screenSize === "xs" ? 36 : 44,
                  borderRadius: 12,
                  background: isDark
                    ? "linear-gradient(135deg, #3b82f6 0%, #2e7cba 100%)"
                    : "linear-gradient(135deg, #2563eb 0%, #2e7cba 100%)",
                  
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
                    animation: "shimmer 2s infinite",
                  }}
                />
                <CheckCircle size={screenSize === "xs" ? 20 : 24} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: screenSize === "xs" ? fonts.body : fonts.subtitle,
                  fontWeight: 800,
                  color: isDark ? "#e0e7ff" : "#1e1b4b",
                  letterSpacing: "-0.5px",
                }}>
                  {language === "ar" ? "المرشحات" : "Filters"}
                </h3>
                <p style={{
                  margin: "0.25rem 0 0 0",
                  fontSize: fonts.small,
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontWeight: 500,
                }}>
                  {language === "ar" ? "ابحث وصفّ النتائج" : "Search and filter results"}
                </p>
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {/* <button
              onClick={clearArchiveFilters}
              style={{
                padding: screenSize === "xs" ? "0.5rem 1rem" : "0.625rem 1.25rem",
                borderRadius: 9,
                border: isDark 
                  ? "1.5px solid rgba(148,163,184,0.3)" 
                  : "1.5px solid rgba(100,116,139,0.2)",
                background: isDark
                  ? "rgba(100,116,139,0.1)"
                  : "rgba(226,232,240,0.5)",
                color: isDark ? "#cbd5e1" : "#475569",
                fontSize: fonts.small,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 200ms ease",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark 
                  ? "rgba(148,163,184,0.15)" 
                  : "rgba(100,116,139,0.15)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDark
                  ? "rgba(100,116,139,0.1)"
                  : "rgba(226,232,240,0.5)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <X size={16} />
              {language === "ar" ? "مسح المرشحات" : "Clear Filters"}
            </button> */}
          </div>

          {/* Filter Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: screenSize === "xs" 
                ? "1fr" 
                : screenSize === "sm" 
                ? "repeat(2, 1fr)" 
                : "repeat(auto-fit, minmax(260px, 1fr))",
              gap: screenSize === "xs" ? "1rem 0.75rem" : "1.5rem 1.25rem",
              alignItems: "flex-end",
            }}
          >
            {/* Search */}
            <div>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 7, 
                fontWeight: 700, 
                fontSize: fonts.small, 
                color: isDark ? "#cbd5e1" : "#475569", 
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                <Eye size={16} style={{ color: isDark ? "#60a5fa" : "#2563eb", flexShrink: 0 }} />
                {language === "ar" ? "بحث" : "Search"}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  placeholder={text[language].archiveSearchPlaceholder}
                  value={archiveSearch}
                  onChange={(e) => setArchiveSearch(e.target.value)}
                  style={{
                    padding: screenSize === "xs" ? "0.6rem 1rem 0.6rem 2.3rem" : "0.75rem 1.25rem 0.75rem 2.5rem",
                    borderRadius: 11,
                    border: isDark ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(0,0,0,0.1)",
                    background: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)",
                    color: isDark ? "#f1f5f9" : "#0f172a",
                    fontSize: fonts.body,
                    width: "100%",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.8)" : "rgba(255,255,255,1)";
                    e.currentTarget.style.borderColor = isDark ? "rgba(100,116,139,0.4)" : "rgba(37,99,235,0.2)";
                    e.currentTarget.style.boxShadow = isDark 
                      ? "0 0 0 3px rgba(59,130,246,0.1), 0 2px 12px rgba(0,0,0,0.08)"
                      : "0 0 0 3px rgba(37,99,235,0.08), 0 2px 12px rgba(0,0,0,0.04)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)";
                    e.currentTarget.style.borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(0,0,0,0.1)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                  }}
                />
                <Eye
                  size={18}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: language === "ar" ? "auto" : screenSize === "xs" ? 8 : 10,
                    right: language === "ar" ? (screenSize === "xs" ? 8 : 10) : "auto",
                    transform: "translateY(-50%)",
                    color: isDark ? "#64748b" : "#cbd5e1",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            {/* Working Company Dropdown */}
            <div>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 7, 
                fontWeight: 700, 
                fontSize: fonts.small, 
                color: isDark ? "#cbd5e1" : "#475569", 
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                <Building size={16} style={{ color: isDark ? "#60a5fa" : "#2563eb", flexShrink: 0 }} />
                {text[language].archiveFilterWorkingCompany}
              </label>
              <ReusableDropdown
                items={[{ key: "", label: language === "ar" ? "الكل" : "All" }, ...archiveWorkingCompanyOptions.map((c) => ({ key: c, label: c }))]}
                value={archiveFilterWorkingCompanySearch}
                onChange={(val) => setArchiveFilterWorkingCompanySearch(val)}
                onSelect={(item) => { setArchiveFilterWorkingCompany(item.key); setArchiveFilterWorkingCompanySearch(item.label); }}
                placeholder={language === "ar" ? "الكل" : "All"}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                maxItems={8}
              />
            </div>

            {/* Department Dropdown */}
            <div>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 7, 
                fontWeight: 700, 
                fontSize: fonts.small, 
                color: isDark ? "#cbd5e1" : "#475569", 
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                <Users size={16} style={{ color: isDark ? "#60a5fa" : "#2563eb", flexShrink: 0 }} />
                {text[language].archiveFilterDepartment}
              </label>
              <ReusableDropdown
                items={[{ key: "", label: language === "ar" ? "الكل" : "All" }, ...archiveDepartmentOptions.map((d) => ({ key: d, label: d }))]}
                value={archiveFilterDepartmentSearch}
                onChange={(val) => setArchiveFilterDepartmentSearch(val)}
                onSelect={(item) => { setArchiveFilterDepartment(item.key); setArchiveFilterDepartmentSearch(item.label); }}
                placeholder={language === "ar" ? "الكل" : "All"}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                maxItems={8}
              />
            </div>

            {/* Date From */}
            <div>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 7, 
                fontWeight: 700, 
                fontSize: fonts.small, 
                color: isDark ? "#cbd5e1" : "#475569", 
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                <Calendar size={16} style={{ color: isDark ? "#60a5fa" : "#2563eb", flexShrink: 0 }} />
                {text[language].archiveDateFrom}
              </label>
              <input
                type="date"
                value={archiveFilterDateFrom}
                onChange={(e) => setArchiveFilterDateFrom(e.target.value)}
                style={{
                  padding: screenSize === "xs" ? "0.6rem 1rem" : "0.75rem 1.25rem",
                  borderRadius: 11,
                  border: isDark ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(0,0,0,0.1)",
                  background: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)",
                  color: isDark ? "#f1f5f9" : "#0f172a",
                  fontSize: fonts.body,
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  fontWeight: 500,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.8)" : "rgba(255,255,255,1)";
                  e.currentTarget.style.borderColor = isDark ? "rgba(100,116,139,0.4)" : "rgba(37,99,235,0.2)";
                  e.currentTarget.style.boxShadow = isDark 
                    ? "0 0 0 3px rgba(59,130,246,0.1), 0 2px 12px rgba(0,0,0,0.08)"
                    : "0 0 0 3px rgba(37,99,235,0.08), 0 2px 12px rgba(0,0,0,0.04)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)";
                  e.currentTarget.style.borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(0,0,0,0.1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Date To */}
            <div>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 7, 
                fontWeight: 700, 
                fontSize: fonts.small, 
                color: isDark ? "#cbd5e1" : "#475569", 
                marginBottom: "0.5rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                <Calendar size={16} style={{ color: isDark ? "#60a5fa" : "#2563eb", flexShrink: 0 }} />
                {text[language].archiveDateTo}
              </label>
              <input
                type="date"
                value={archiveFilterDateTo}
                onChange={(e) => setArchiveFilterDateTo(e.target.value)}
                style={{
                  padding: screenSize === "xs" ? "0.6rem 1rem" : "0.75rem 1.25rem",
                  borderRadius: 11,
                  border: isDark ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(0,0,0,0.1)",
                  background: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)",
                  color: isDark ? "#f1f5f9" : "#0f172a",
                  fontSize: fonts.body,
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  fontWeight: 500,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.8)" : "rgba(255,255,255,1)";
                  e.currentTarget.style.borderColor = isDark ? "rgba(100,116,139,0.4)" : "rgba(37,99,235,0.2)";
                  e.currentTarget.style.boxShadow = isDark 
                    ? "0 0 0 3px rgba(59,130,246,0.1), 0 2px 12px rgba(0,0,0,0.08)"
                    : "0 0 0 3px rgba(37,99,235,0.08), 0 2px 12px rgba(0,0,0,0.04)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.9)";
                  e.currentTarget.style.borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(0,0,0,0.1)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Export Button */}
            {isAdminRole && (
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-start" }}>
                <button
                  onClick={handleExportArchive}
                  disabled={loading}
                  style={{
                    ...internalStyles.secondaryBtn,
                    opacity: loading ? 0.5 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                    width: screenSize === "xs" ? "100%" : "auto",
                    justifyContent: "center",
                  }}
                >
                  <Download size={18} />
                  {loading ? text[language].exportingArchive : text[language].exportArchive}
                </button>
              </div>
            )}
          </div>

          {/* Modal Animations */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translate(-50%, -50%) translateY(30px);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) translateY(0);
              }
            }
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}</style>
        </div>

        {/* Archived Employees Table */}
        <div style={internalStyles.tableContainer}>
          <table style={internalStyles.table}>
            <thead>
              <tr style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                <th style={internalStyles.tableHeader}>{text[language].employee}</th>
                {screenSize !== "xs" && (
                  <th style={internalStyles.tableHeader}>
                    {text[language].department}
                  </th>
                )}
                {screenSize !== "xs" && screenSize !== "sm" && (
                  <th style={internalStyles.tableHeader}>{text[language].archiveFilterWorkingCompany}</th>
                )}
                {screenSize !== "xs" && screenSize !== "sm" && (
                  <th style={internalStyles.tableHeader}>{text[language].archivedDate}</th>
                )}
                {screenSize !== "xs" && (
                  <th style={internalStyles.tableHeader}>
                    {text[language].status}
                  </th>
                )}
                <th style={internalStyles.tableHeader}>{text[language].actions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredArchivedEmployees.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      screenSize === "xs"
                        ? "3"
                        : screenSize === "sm"
                          ? "4"
                          : "6"
                    }
                    style={{
                      ...internalStyles.tableCell,
                      textAlign: "center",
                      padding: screenSize === "xs" ? "2rem" : "2.5rem",
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(26, 26, 26, 0.5)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: spacing.gap,
                      }}
                    >
                      <Archive
                        size={screenSize === "xs" ? 32 : 48}
                        color={
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(26, 26, 26, 0.3)"
                        }
                      />
                      <div>{text[language].noArchivedEmployees}</div>
                      <div style={{ fontSize: fonts.small, opacity: "0.7" }}>
                        {text[language].employeesArchivedMessage}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredArchivedEmployees.map((emp) => (
                  <tr
                    key={emp.id}
                    style={{
                      ...internalStyles.tableRow,
                      background:
                        theme === "dark"
                          ? "rgba(107, 114, 128, 0.05)"
                          : "rgba(107, 114, 128, 0.02)",
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                    }}
                  >
                    {/* Employee Name */}
                    <td style={internalStyles.tableCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                        <div
                          style={{
                            position: "relative",
                            width: screenSize === "xs" ? 26 : 32,
                            height: screenSize === "xs" ? 26 : 32,
                            borderRadius: "50%",
                            flexShrink: 0,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: genderAvatarBg(emp.gender),
                            color: "#fff",
                          }}
                        >
                          <User size={screenSize === "xs" ? 14 : 17} strokeWidth={2.25} />
                          {profilePics[emp.infinityCode || emp.id] && (
                            <img
                              src={profilePics[emp.infinityCode || emp.id]}
                              alt=""
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                              style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </div>
                        <span style={{ fontWeight: "500" }}>{language === 'ar' ? (emp.arabicName || emp.name) : emp.name}</span>
                      </div>
                    </td>

                    {/* Department - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={internalStyles.tableCell}>{emp.department || "N/A"}</td>
                    )}

                    {/* Working Company - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={internalStyles.tableCell}>
                        {emp.workingCompany || (
                          <span style={{ opacity: 0.4 }}>—</span>
                        )}
                      </td>
                    )}

                    {/* Archived Date - Hidden on mobile and tablet */}
                    {screenSize !== "xs" && screenSize !== "sm" && (
                      <td style={internalStyles.tableCell}>
                        {emp.archivedDate
                          ? new Date(emp.archivedDate).toLocaleDateString(language === 'ar' ? "ar-EG" : "en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : "N/A"}
                      </td>
                    )}

                    {/* Status - Hidden on mobile */}
                    {screenSize !== "xs" && (
                      <td style={internalStyles.tableCell}>
                        {getStatusBadge(emp.status)}
                      </td>
                    )}

                    {/* Actions */}
                    <td style={internalStyles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          gap: screenSize === "xs" ? "0.25rem" : "0.5rem",
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {/* <button
                          style={{ ...internalStyles.actionBtn, ...internalStyles.viewBtn }}
                          title={text[language].viewDetails}
                          onClick={() => handleViewEmployee(emp)}
                          onMouseEnter={(e) =>
                            handleActionBtnMouseEnter(e, "view")
                          }
                          onMouseLeave={(e) =>
                            handleActionBtnMouseLeave(e, "view")
                          }
                        >
                          <Eye size={screenSize === "xs" ? 12 : 14} />
                        </button> */}
                        {/* <button
                          style={{ ...internalStyles.actionBtn, ...internalStyles.editBtn }}
                          title={text[language].editEmployee}
                          onClick={() => handleEditEmployee(emp)}
                          onMouseEnter={(e) =>
                            handleActionBtnMouseEnter(e, "edit")
                          }
                          onMouseLeave={(e) =>
                            handleActionBtnMouseLeave(e, "edit")
                          }
                        >
                          <Edit size={screenSize === "xs" ? 12 : 14} />
                        </button> */}
                        {canRestoreEmployee && (
                          <button
                            style={{ ...internalStyles.actionBtn, ...internalStyles.restoreBtn }}
                            title={text[language].restoreEmployee || 'Restore Employee'}
                            onClick={() => handleRestoreEmployee(emp.id)}
                          >
                            <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                        {canDeleteEmployee && (
                          <button
                            style={{ ...internalStyles.actionBtn, ...internalStyles.deleteBtn, }}
                            title={text[language].deleteEmployee}
                            onClick={() => handleDeleteEmployee(emp.id)}
                          >
                            <Trash2 size={screenSize === "xs" ? 12 : 14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Archive Actions */}
        {archivedEmployees.length > 0 && (
          <div
            style={{
              marginTop: spacing.margin,
              padding: spacing.padding,
              background:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.02)",
              borderRadius: screenSize === "xs" ? "0.75rem" : "0.75rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border:
                theme === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.05)",
              flexDirection: screenSize === "xs" ? "column" : "row",
              gap: screenSize === "xs" ? spacing.gap : "0",
            }}
          >
            <div
              style={{
                color:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.7)"
                    : "rgba(26, 26, 26, 0.6)",
                fontSize: fonts.body,
                textAlign: screenSize === "xs" ? "center" : "left",
              }}
            >
              {filteredArchivedEmployees.length}
              {filteredArchivedEmployees.length === 1
                ? text[language].archivedEmployee
                : text[language].archivedEmployees}
            </div>
            <div
              style={{
                display: "flex",
                gap: spacing.gap,
                flexWrap: "wrap",
                justifyContent: screenSize === "xs" ? "center" : "flex-end",
              }}
            >
              {canRestoreEmployee && (
                <button
                  style={{
                    ...internalStyles.secondaryBtn,
                    fontSize: fonts.small,
                    padding:
                      screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                  }}
                  onClick={handleRestoreAllArchivedEmployees}
                >
                  <RotateCcw size={screenSize === "xs" ? 12 : 14} />
                  {screenSize !== "xs" && text[language].restoreAll}
                </button>
              )}
              {/* <button
                style={{
                  ...internalStyles.secondaryBtn,
                  fontSize: fonts.small,
                  padding:
                    screenSize === "xs" ? "0.5rem 0.75rem" : "0.5rem 1rem",
                  color: "#ef4444",
                  borderColor: "rgba(239, 68, 68, 0.3)",
                }}
                onClick={handleDeleteAllArchived}
              >
                <Trash2 size={screenSize === "xs" ? 12 : 14} />
                {screenSize !== "xs" && text[language].deleteAll}
              </button> */}
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ArchiveModal;

